// Market ladder card -- one token's one-touch ladder. Renders a banner
// (token + ccy + live spot), four banner tiles -- Open/High/Low (from the
// month-to-date spot series) + Latest (live spot, aligning with the four rung
// columns) -- and the rung table -- Level . vs Spot . Imm (BS) . Imm (Local
// Vol) -- with a spot marker row dividing upper from lower rungs. The vs-Spot
// cell is each rung's signed % distance from live spot; Imm (BS) is the faded
// reference column and Imm (Local Vol) is the headline (keeps the prob-band
// chip). A HIT rung reads a muted 100% in both model columns.
//
// START-OF-MONTH ANCHOR (IM-1594): the ladder is FIXED for the month, pinned
// to the start-of-month spot upstream. Triggered rungs (an upper rung whose
// level the MTD high has reached, or a lower rung the MTD low has reached)
// carry a HIT badge and read 100% -- already touched. The spot-marker row
// shows the LIVE spot against the fixed ladder; live spot can sit anywhere
// relative to the month-open rungs.
//
// MarketView prop (token / ccy / live spot / somSpot / mtdHigh / mtdLow /
// rungs / covered / mtdStatus) is supplied by the multi-market provider, so
// the card is presentational.
//
// Graceful states:
//   - uncovered (no vol surface in the registry -- HYPE the likely case):
//     banner + "No vol surface" empty state.
//   - mtdStatus 'error' (the MTD spot series errored or came back empty):
//     banner + "No MTD data" empty state -- the ladder has no anchor.
// One degraded market never breaks the board.
//
// Kalshi "Mkt"/"Edge" columns are out of slice scope; the table renders the
// Immersive model columns only, keeping the mock chrome so the later Kalshi
// join is additive.
import { FC, ReactNode, useCallback, useContext, useEffect, useRef } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import usePersistentState from '../../providers/use-persistent-state';
import { faLocationCrosshairs } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Number from '../../components/v2/number';
import CryptoIcon from '../../components/v2/crypto-icon';
import EmptyState from '../../components/v2/empty-state';
import {
  dilettaPanelSx,
  DILETTA_BANNER_BG,
  DILETTA_TITLE,
  DILETTA_DIVIDER,
} from '../../components/v2/widgets/exotics-shared/diletta-chrome';
import {
  displayTouchProb,
  MarketProbabilitiesContext,
  MarketView,
  RungQuote,
} from '../../providers/market-probabilities-provider';
import { PricerHandoffContext } from '../../providers/pricer-handoff/pricer-handoff-provider';
import { buildHandoffSpec } from './handoff';
import {
  fmtElapsed,
  formatExpiryStamp,
  formatTimeToExpiry,
  freshnessBand,
  pctFromSpot,
  probBand,
  spreadPp,
} from './ladder';
import {
  ACCENT_SOFT,
  ACCENT_SOFT_HOVER_FILL,
  BAND_COLOUR,
  BORDER_SUBTLE,
  FRESH_DOT_COLOUR,
  SCROLLBAR_THUMB,
  SPOT_ACCENT,
  SPOT_ROW_FILL,
  TEXT_DIM,
  TEXT_DISABLED,
  TEXT_FAINT,
  TEXT_GHOST,
  TEXT_MUTED,
  TEXT_PRIMARY,
  TEXT_SUBTLE,
  TILE_BG,
} from './chrome';

// ONE shared column-track definition for the rung table. The header row, every
// RungRow and the SpotRow all render on these identical tracks, so vs-Spot / BS /
// LV / SV sit on clean vertical edges regardless of chip / spread / HIT chrome:
//   Level | vs Spot | Imm (BS) | Imm (Local Vol) | Imm (Stoch Vol)
// The five columns are EQUALLY spaced (repeat(5, 1fr)) so the panel's numbers
// distribute evenly across the card instead of bunching the numeric columns
// against the right edge. The LV/SV cells hold "100.00%" + the prob-band chip in
// their left gutter without wrapping; the (+x.xx%) spread and the touched marker
// are stacked as a muted second line inside the cell, so they never widen the
// track or shift the headline % off its edge.
const RUNG_GRID = 'repeat(5, minmax(0, 1fr))';

// Per-row grid frame, shared by header / rung / spot so column edges align.
const ROW_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: RUNG_GRID,
  alignItems: 'center',
  columnGap: 0,
  px: 1.75,
} as const;

// Fixed scroll-viewport for the rung table. The banner, the four OHLC+Latest
// tiles and the column-header row stay PINNED outside this; only the rung table
// (upper rungs + spot row + lower rungs) scrolls inside it, so all eight cards
// keep an identical overall height and the 4x2 board grid stays uniform however
// deep an individual market's band-derived ladder runs. Sized to ~12-13 rungs
// at once (each RungRow is ~32px with its reserved second line) -- ~1.5x the
// initial scroll height so the panels read as large as the pre-scroll layout;
// the near-the-money rungs are visible without scrolling and the deep tail
// (down to ~$50k on BTC) is reached by scrolling.
// Default rung-viewport height. +50% over the prior fixed 402 so cards read
// large out of the box; the per-card resize handle (below) lets the user grow
// or shrink each one independently, persisted to localStorage.
const DEFAULT_RUNG_VIEWPORT_HEIGHT = 603;

// Floor for the resizable viewport -- keeps the near-money rungs + spot row
// legible however small the user drags a card.
const MIN_RUNG_VIEWPORT_HEIGHT = 240;

// localStorage namespace for the per-card viewport height. usePersistentState
// keys each card under `${path}/${token}`, so BTC/ETH/SOL/... each remember
// their own height across reloads.
const LADDER_HEIGHT_PERSIST_PATH = ['MARKET_PROBABILITIES', 'ladderHeight'];

// Subtle scrollbar matching the dark chrome -- a thin muted thumb on a
// transparent track, so the deep ladder reads as scrollable without a heavy
// native bar fighting the panel. WebKit + Firefox both covered. Height is
// applied per-card at the call site (persisted, resizable), not fixed here.
//
// overflowX MUST be non-visible for the CSS `resize` grip to render: a resize
// handle only appears when overflow is not `visible` on BOTH axes (Chrome
// requirement). overflowY:'auto' alone leaves overflow-x computed as visible,
// so the grip silently never draws. 'hidden' on X (not 'auto') keeps vertical
// scrolling while suppressing any horizontal scrollbar -- rung rows never
// overflow horizontally (grid tracks are minmax(0,1fr)).
const RUNG_SCROLL_SX = {
  overflowY: 'auto' as const,
  overflowX: 'hidden' as const,
  scrollbarWidth: 'thin' as const,
  scrollbarColor: `${SCROLLBAR_THUMB} transparent`,
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: SCROLLBAR_THUMB,
    borderRadius: 3,
  },
} as const;

// Shared column-header type style. The narrow portrait card keeps these tiny;
// the BS header is additionally muted (the reference column).
const HEAD_SX = {
  fontSize: 8,
  color: TEXT_MUTED,
  textTransform: 'uppercase' as const,
  letterSpacing: '.05em',
  fontWeight: 600,
};

// Format a price at the market's FIXED precision: thousands separators + the
// same `decimals` fractional digits for every price the card renders (levels,
// OHLC/Latest tiles, spot band), so they read consistently per market rather
// than ragged (AVAX 10.5/10.0/9.5 not 10.5/10/9.5). Em-dash for null/non-finite.
const fmtPrice = (v: number | null, decimals: number): string =>
  v !== null && globalThis.Number.isFinite(v)
    ? v.toLocaleString('en-GB', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : '—';

// Signed % distance of a level from live spot, e.g. "+4.80%" / "-3.20%".
// Fixed 2-dp to match every other percent on the card (no ragged 1-dp/3-dp
// mixing). Em-dash when spot is unknown (pctFromSpot returns null).
const fmtPct = (v: number | null): string =>
  v !== null && globalThis.Number.isFinite(v) ? `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` : '—';

// Prob-band chip. Sits in the LV cell's fixed left gutter (laid out by LvCell's
// grid), so it never pushes the headline % off the track edge. No own margin --
// the gutter owns the spacing.
const ProbChip: FC<{ prob: number }> = ({ prob }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '2px',
      backgroundColor: BAND_COLOUR[probBand(prob)],
    }}
  />
);

// A HIT rung is resolved (already touched, prob = 100%) -- it's settled
// context, not signal, so it recedes: a quiet muted marker, no filled badge
// and no amber. Rendered as the LV cell's muted second line (right-aligned,
// under the 100%) so the headline figure keeps the track edge. Abbreviated to
// a bare "touched" -- the tick is dropped to keep the narrow track honest.
const HitMarker: FC = () => (
  <Box
    component="span"
    sx={{
      fontSize: 7.5,
      fontWeight: 400,
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      color: TEXT_FAINT,
    }}
  >
    {'touched'}
  </Box>
);

// Black-Scholes column: the quiet REFERENCE. Muted colour, smaller tabular
// figures, no prob-band chip. It's the one to shrink when the narrow portrait
// card crowds. HIT reads a muted 100%, consistent with the settled-row
// treatment. Right-aligned to the BS track edge; the figure is the only thing
// in this track, so there is nothing to push it off the edge.
const BsCell: FC<{ rung: RungQuote; hit: boolean }> = ({ rung, hit }) => {
  // In-situ render: a HELD value always renders, even with an error flagged on
  // the rung (an error/timeout on an already-priced cell must not blank it --
  // the value stays until a fresh result replaces it in situ). The em-dash
  // shows ONLY when there is genuinely no value (a never-priced cell). The
  // error flag itself is an internal signal here, NOT a blanking trigger.
  // displayTouchProb applies the HIT -> 100% override keyed on the EFFECTIVE
  // (variant-resolved) hit passed in -- OTC suppresses it, so OTC shows the
  // backend pv as-is; otherwise the feed's BS pv, or null for a never-priced cell.
  const v = displayTouchProb(rung, 'bs', hit);
  const body = hit ? (
    <Box component="span" sx={{ color: TEXT_FAINT }}>
      <Number value={v ?? 1} format="percent" role="caption" />
    </Box>
  ) : v === null ? (
    <Box component="span" sx={{ color: TEXT_GHOST }}>
      {'—'}
    </Box>
  ) : (
    <Box component="span" sx={{ color: TEXT_MUTED }}>
      <Number value={v} format="percent" role="caption" />
    </Box>
  );
  return (
    <Box
      sx={{
        textAlign: 'right',
        fontWeight: 400,
        fontSize: 9.5,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {body}
    </Box>
  );
};

// Signed LV-minus-BS spread annotation in percentage points, e.g. "(+3.00%)".
// The smile signal: positive => Local Vol adds touch probability over
// Black-Scholes, negative => removes it. Faded/small -- NOT a headline. Lives
// on the LV cell's second line (right-aligned, under the headline %), so it
// never shifts the headline figure off the track edge nor widens the column.
// Renders only when spreadPp returns a value (both probs present).
const SpreadAnnotation: FC<{ pp: number }> = ({ pp }) => (
  <Box
    component="span"
    sx={{
      fontSize: 8.5,
      fontWeight: 400,
      color: TEXT_DIM,
      fontVariantNumeric: 'tabular-nums',
    }}
  >
    ({pp >= 0 ? '+' : ''}
    {pp.toFixed(2)}%)
  </Box>
);

// Shared HEADLINE column chrome for the model columns (Local Vol + Stoch Vol):
// full prominence, prob-band chip, two stacked right-aligned lines.
//
//   line 1  [ chip gutter | headline % ]  -- inner grid '1fr auto': the chip
//           right-aligns into the 1fr gutter, the % pins the track edge, so the
//           chip never shifts the figure off the column.
//   line 2  the muted (model - BS) spread, or the settled-row "touched" marker on
//           a HIT. Its height is reserved on every state so live / HIT / error
//           rows keep an identical height and land on the same column edges.
// HIT reads a muted 100% (line 1) + touched marker (line 2). A null value (never
// priced / missing model leg) reads a right-aligned em-dash with the same
// reserved second line.
const HeadlineCell: FC<{ value: number | null; spread: number | null; hit: boolean }> = ({
  value,
  spread,
  hit,
}) => {
  const headline = hit ? (
    <Box
      component="span"
      sx={{ fontWeight: 400, color: TEXT_DIM, fontVariantNumeric: 'tabular-nums' }}
    >
      <Number value={value ?? 1} format="percent" role="caption" />
    </Box>
  ) : value === null ? (
    <Box component="span" sx={{ color: TEXT_DISABLED, fontFamily: 'monospace', fontSize: 11 }}>
      {'—'}
    </Box>
  ) : (
    <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
      <Number value={value} format="percent" role="caption" />
    </Box>
  );

  const showChip = !hit && value !== null && globalThis.Number.isFinite(value);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          columnGap: 0.625,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          {showChip ? <ProbChip prob={value as number} /> : null}
        </Box>
        <Box sx={{ textAlign: 'right' }}>{headline}</Box>
      </Box>
      {/* Reserved second line: keeps row height constant across live/HIT/error. */}
      <Box sx={{ minHeight: 11, textAlign: 'right', lineHeight: '11px' }}>
        {hit ? <HitMarker /> : spread !== null ? <SpreadAnnotation pp={spread} /> : null}
      </Box>
    </Box>
  );
};

// Local Vol column: the primary HEADLINE. displayTouchProb applies the HIT->100%
// override and the BS-only-board fallback (probLv ?? probBs); the spread is the
// (probLv - probBs) smile signal, shown only when both legs are priced.
const LvCell: FC<{ rung: RungQuote; hit: boolean }> = ({ rung, hit }) => {
  const value = displayTouchProb(rung, 'lv', hit);
  const spread =
    !hit && rung.probLv !== null && rung.probBs !== null
      ? spreadPp(rung.probLv, rung.probBs)
      : null;
  return <HeadlineCell value={value} spread={spread} hit={hit} />;
};

// Stoch Vol column: same headline chrome as Local Vol, sourced from probSv. No BS
// fallback -- a missing sv leg reads an em-dash (graceful degrade) rather than
// borrowing BS. Spread is (probSv - probBs). HIT reads 100% here too -- a
// historical touch is model-independent.
const SvCell: FC<{ rung: RungQuote; hit: boolean }> = ({ rung, hit }) => {
  const value = displayTouchProb(rung, 'sv', hit);
  const spread =
    !hit && rung.probSv !== null && rung.probBs !== null
      ? spreadPp(rung.probSv, rung.probBs)
      : null;
  return <HeadlineCell value={value} spread={spread} hit={hit} />;
};

// Clickable wrapper for a rung's model cell (IM-1955). A model-cell click hands
// that rung + the clicked model to the pricer. Subtle affordance only — cursor
// pointer + a faint hover fill (reused theme token); the cell's colour / 100% /
// spread semantics inside are untouched. Keyboard-operable (Enter / Space).
const ModelPickCell: FC<{ label: string; onPick: () => void; children: ReactNode }> = ({
  label,
  onPick,
  children,
}) => (
  <Box
    role="button"
    tabIndex={0}
    aria-label={label}
    onClick={onPick}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onPick();
      }
    }}
    sx={{
      cursor: 'pointer',
      borderRadius: '3px',
      // Negative inline margin + matching padding keeps the numbers on their
      // existing column edges while giving the hover fill breathing room.
      mx: -0.375,
      px: 0.375,
      transition: 'background-color .12s',
      '&:hover': { backgroundColor: ACCENT_SOFT_HOVER_FILL },
      '&:focus-visible': { outline: `1px solid ${SPOT_ACCENT}`, outlineOffset: '1px' },
    }}
  >
    {children}
  </Box>
);

const RungRow: FC<{
  rung: RungQuote;
  spot: number | null;
  decimals: number;
  isOtc?: boolean;
  onPick?: (rung: RungQuote, model: 'bs' | 'lv' | 'sv') => void;
}> = ({ rung, spot, decimals, isOtc = false, onPick }) => {
  const hit = isOtc ? false : rung.hit;
  const pct = pctFromSpot(rung.level, spot);
  return (
    <Box
      sx={{
        ...ROW_GRID_SX,
        py: 0.625,
        borderTop: `1px solid ${BORDER_SUBTLE}`,
        backgroundColor: 'transparent',
        opacity: hit ? 0.7 : 1,
      }}
    >
      <Typography
        sx={{
          minWidth: 0,
          fontFamily: 'monospace',
          fontSize: 11,
          color: hit ? TEXT_DIM : TEXT_PRIMARY,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {fmtPrice(rung.level, decimals)}
      </Typography>
      <Typography
        sx={{
          textAlign: 'right',
          fontFamily: 'monospace',
          fontSize: 10,
          color: pct === null ? TEXT_DISABLED : TEXT_SUBTLE,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {fmtPct(pct)}
      </Typography>
      <ModelPickCell
        label={`Price one-touch ${fmtPrice(rung.level, decimals)} in pricer (Black-Scholes)`}
        onPick={() => onPick?.(rung, 'bs')}
      >
        <BsCell rung={rung} hit={hit} />
      </ModelPickCell>
      <ModelPickCell
        label={`Price one-touch ${fmtPrice(rung.level, decimals)} in pricer (Local Vol)`}
        onPick={() => onPick?.(rung, 'lv')}
      >
        <LvCell rung={rung} hit={hit} />
      </ModelPickCell>
      <ModelPickCell
        label={`Price one-touch ${fmtPrice(rung.level, decimals)} in pricer (Stoch Vol)`}
        onPick={() => onPick?.(rung, 'sv')}
      >
        <SvCell rung={rung} hit={hit} />
      </ModelPickCell>
    </Box>
  );
};

// Spot marker row. Shares the rung grid so its left edge lines up with the
// Level column; the "spot" label sits in the Level track and the live spot
// value spans the four right tracks (vs Spot / BS / LV / SV), right-aligned to
// the SV edge -- the same edge the headline % land on.
const SpotRow: FC<{ spot: number | null; decimals: number }> = ({ spot, decimals }) => (
  <Box
    sx={{
      ...ROW_GRID_SX,
      py: 0.625,
      backgroundColor: SPOT_ROW_FILL,
      borderTop: `1px solid ${SPOT_ACCENT}`,
      borderBottom: `1px solid ${SPOT_ACCENT}`,
    }}
  >
    <Typography
      sx={{
        minWidth: 0,
        fontFamily: 'monospace',
        fontSize: 11,
        fontWeight: 700,
        color: SPOT_ACCENT,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {'▸'} spot
    </Typography>
    <Typography
      sx={{
        gridColumn: '2 / -1',
        textAlign: 'right',
        fontFamily: 'monospace',
        fontSize: 11,
        fontWeight: 700,
        color: SPOT_ACCENT,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {fmtPrice(spot, decimals)}
    </Typography>
  </Box>
);

// Shared tile for the top OHLC+Latest row and the bottom ATM/RR/expiry row.
// A NUMBER value is formatted via fmtPrice (locale thousands + the market's
// fixed `decimals` / em-dash); a STRING value (the footer's preformatted vol /
// RR / stamp / countdown) renders verbatim, and a null reads the muted em-dash.
// Same chrome either way so the two rows mirror each other exactly. `decimals`
// is unused for string values (the footer tiles pass preformatted strings).
const Tile: FC<{ label: string; value: number | string | null; decimals?: number }> = ({
  label,
  value,
  decimals = 0,
}) => {
  const text = typeof value === 'number' ? fmtPrice(value, decimals) : (value ?? '—');
  const muted = value === null || value === '—';
  return (
    <Box sx={{ flex: 1, backgroundColor: TILE_BG, px: 1.125, py: 0.75 }}>
      <Typography
        sx={{ fontSize: 8, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '.06em' }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'monospace',
          fontSize: 11.5,
          color: muted ? TEXT_DISABLED : TEXT_PRIMARY,
          mt: 0.25,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

// Footer tile formatters. ATM vol / 25RR arrive as decimals off the smile
// summary (atmVol 0.624 -> "62.4%", rr25 -0.0321 -> "-3.21%"); a null (no smile
// yet, uncovered, or an unpriceable tenor) renders the em-dash so the footer
// never blanks the card. The expiry stamp + countdown are pure functions of the
// selected expiry and ALWAYS render (they don't depend on pricing).
const fmtAtmVol = (v: number | null): string | null =>
  v !== null && globalThis.Number.isFinite(v) ? `${(v * 100).toFixed(1)}%` : null;

const fmtRr25 = (v: number | null): string | null => {
  if (v === null || !globalThis.Number.isFinite(v)) return null;
  const pp = v * 100;
  return `${pp >= 0 ? '+' : ''}${pp.toFixed(2)}%`;
};

// Banner shows token + ccy only. The live spot is intentionally NOT repeated
// here -- it already appears in the Latest tile (top tile row) and on the
// spot-marker band in the middle of the ladder, so a third copy in the banner
// is redundant.
const Banner: FC<{ token: string; ccy: string | null; onCentre?: () => void }> = ({
  token,
  ccy,
  onCentre,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{ backgroundColor: DILETTA_BANNER_BG, px: 1.625, py: 1.125 }}
  >
    {/* Token logo + name. The icon reuses the app-wide CryptoIcon component
        (resolved by token symbol, with its own GENERIC fallback chain), sized
        to sit inside the banner title chrome. Sits just left of the name. */}
    <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.75, lineHeight: 0 }}>
      <CryptoIcon symbol={token} size={16} />
    </Box>
    <Typography
      sx={{ color: DILETTA_TITLE, fontWeight: 700, fontSize: 13, letterSpacing: '.04em' }}
    >
      {token}
      {ccy !== null ? (
        <>
          {' '}
          <Box component="span" sx={{ color: ACCENT_SOFT, fontWeight: 400, fontSize: 10 }}>
            {'·'} {ccy}
          </Box>
        </>
      ) : null}
    </Typography>
    {/* Re-centre ("locate") arrow, top-right -- scrolls the rung viewport so the
        spot-marker band is centred. Rendered only when onCentre is supplied
        (the card omits it in the uncovered / no-MTD empty states, where there is
        no spot to centre on). Small, muted, fits the banner chrome. */}
    {onCentre ? (
      <>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Centre on spot" placement="top" arrow>
          <Box
            component="button"
            type="button"
            onClick={onCentre}
            aria-label="Centre on spot"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0.5,
              border: 'none',
              borderRadius: '4px',
              background: 'transparent',
              color: ACCENT_SOFT,
              cursor: 'pointer',
              lineHeight: 0,
              transition: 'color .12s, background-color .12s',
              '&:hover': { color: DILETTA_TITLE, backgroundColor: ACCENT_SOFT_HOVER_FILL },
            }}
          >
            <FontAwesomeIcon icon={faLocationCrosshairs} fontSize={11} />
          </Box>
        </Tooltip>
      </>
    ) : null}
  </Stack>
);

// Footer freshness readout: a small status dot + "time since last update",
// sitting to the RIGHT of the footer caption. Elapsed is computed live off the
// shared `now` tick (no own timer) against the panel's last-updated stamp; the
// dot bands green/amber/red by age. Reads a muted em-dash with NO dot when the
// panel has never updated (lastUpdated null) -- a cold first paint.
const FreshnessReadout: FC<{ lastUpdated: number | null; now: Date }> = ({ lastUpdated, now }) => {
  if (lastUpdated === null) {
    return <Typography sx={{ color: TEXT_GHOST, fontSize: 8.5 }}>{'—'}</Typography>;
  }
  const elapsed = now.getTime() - lastUpdated;
  const band = freshnessBand(elapsed);
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: FRESH_DOT_COLOUR[band],
        }}
      />
      <Typography sx={{ color: TEXT_MUTED, fontSize: 8.5, fontVariantNumeric: 'tabular-nums' }}>
        {fmtElapsed(elapsed)}
      </Typography>
    </Stack>
  );
};

const MarketLadderCard: FC<{
  market: MarketView;
  expiry: string;
  now: Date;
  variant?: 'prediction' | 'otc';
  useYearAnchor?: boolean;
}> = ({ market, expiry, now, variant = 'prediction', useYearAnchor = false }) => {
  const isOtc = variant === 'otc';
  const {
    token,
    ccy,
    baseQuote,
    spot,
    somSpot,
    mtdHigh,
    mtdLow,
    soySpot,
    ytdHigh,
    ytdLow,
    rungs,
    yearRungsProjected,
    covered,
    mtdStatus,
    ytdStatus,
    atmVol,
    rr25,
    lastUpdated,
    decimals,
    ladder,
  } = market;

  const anchorSpot = useYearAnchor ? soySpot : somSpot;
  const anchorHigh = useYearAnchor ? ytdHigh : mtdHigh;
  const anchorLow = useYearAnchor ? ytdLow : mtdLow;

  // Click-to-pricer (IM-1955). A model-cell click builds a one-touch spec from
  // the rung + this card's context (token, selected expiry, board settlement)
  // and opens it in the Exotics Pricer with that model pre-selected. The FE only
  // constructs the instrument — the pricer prices it live via generic_pricer.
  const { openInPricer } = useContext(PricerHandoffContext);
  const { settlement } = useContext(MarketProbabilitiesContext);
  const handlePick = useCallback(
    (rung: RungQuote, model: 'bs' | 'lv' | 'sv') => {
      openInPricer(
        buildHandoffSpec(rung, model, {
          // Lock/price on the registry-resolved vol-surface base_quote (e.g.
          // 'XRP_USDC'), NOT the MP display token ('XRP'). The pricer's token
          // lock, TODAY tiles, live surface and generic_pricer all key on
          // base_quote; the bare display token fails to resolve for alts.
          token: baseQuote ?? token,
          // Display token drives the per-token tab name only.
          displayToken: token,
          expiryIso: expiry,
          onTouch: settlement.onTouch,
          inQuote: settlement.inQuote,
        }),
      );
    },
    [openInPricer, baseQuote, token, expiry, settlement.onTouch, settlement.inQuote],
  );

  // Auto-centre the live-spot row in the fixed scroll viewport. The viewport
  // (scrollRef) holds the upper rungs + spot row + lower rungs; spotRowRef tags
  // the spot marker. We centre on INITIAL MOUNT and whenever the RUNG SET
  // rebuilds (anchor / expiry / band-depth change), so the near-the-money rungs
  // are visible without scrolling.
  //
  // Gating (the load-bearing bit): the effect deps are the rung-set SIGNATURE
  // (the level list + somSpot) -- deliberately NOT live `spot`. Live spot ticks
  // every refresh and drives the marker value, but it must NOT recentre the
  // viewport, or it would yank the user's scroll position mid-read. The rung
  // levels are FIXED for the month (SoM anchor), so this signature is stable
  // across price ticks and only changes on a genuine ladder rebuild.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const spotRowRef = useRef<HTMLDivElement | null>(null);
  const rungSignature = `${somSpot ?? ''}|${rungs.map((r) => r.level).join(',')}`;

  // Per-card rung-viewport height. Persisted per token (localStorage), so each
  // card resizes independently and its size survives reload. Seeds at the +50%
  // default until the user drags the resize handle.
  const [viewportHeight, setViewportHeight] = usePersistentState<number>(
    LADDER_HEIGHT_PERSIST_PATH,
    token,
    DEFAULT_RUNG_VIEWPORT_HEIGHT,
  );

  // Sync the native CSS resize (drag handle on the scroll box) back into the
  // persisted height, SNAPPED to whole rung-rows so a card always shows complete
  // rungs and never a clipped partial row.
  //
  // The observer measures, straight off the DOM, one rung row (STEP) and the
  // spot row (BASE -- the single non-rung element living inside the viewport),
  // then snaps the dragged height to round((measured - BASE) / STEP) * STEP +
  // BASE (floored at MIN, >= one rung). It writes the snapped value to state;
  // because viewportHeight drives the box height via a React-controlled inline
  // style (see the render), the re-render authoritatively overrides the raw drag
  // height with the snapped one -- that's what makes the card visibly step onto
  // a row boundary. The rAF write covers the case where the snapped height
  // EQUALS current state (no re-render fires): it pins the DOM to the boundary
  // directly. The >2px guard swallows the initial observe and sub-pixel jitter
  // so this never loops against the height it just set. No-op in the empty
  // states (scrollRef unset, no viewport rendered).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const measured = Math.round(el.offsetHeight);
      if (measured <= 0) return;
      const spotEl = spotRowRef.current;
      // First child that is NOT the spot-row wrapper is a rung row -> STEP.
      let rungEl: HTMLElement | null = null;
      for (const child of Array.from(el.children)) {
        if (child !== spotEl && child instanceof HTMLElement) {
          rungEl = child;
          break;
        }
      }
      const step = rungEl ? Math.round(rungEl.offsetHeight) : 0;
      const base = spotEl ? Math.round(spotEl.offsetHeight) : 0;
      const snapped =
        step > 0
          ? Math.max(
              MIN_RUNG_VIEWPORT_HEIGHT,
              Math.max(1, Math.round((measured - base) / step)) * step + base,
            )
          : Math.max(MIN_RUNG_VIEWPORT_HEIGHT, measured);
      if (Math.abs(measured - snapped) > 2) {
        requestAnimationFrame(() => {
          const node = scrollRef.current;
          if (node) node.style.height = `${snapped}px`;
        });
      }
      setViewportHeight((prev) => (Math.abs(prev - snapped) > 2 ? snapped : prev));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [setViewportHeight]);

  // The centring calc, extracted so BOTH the rung-set-rebuild effect AND the
  // banner's locate button call the SAME logic: scroll the viewport so the
  // spot-marker band sits at its vertical centre. No-op if either ref is unset
  // (empty state / pre-layout).
  const centreOnSpot = useCallback(() => {
    const container = scrollRef.current;
    const spotRow = spotRowRef.current;
    if (!container || !spotRow) return;
    // Offset of the spot-marker row's TOP relative to the scroll container's
    // content origin. Computed from bounding rects + current scrollTop rather
    // than offsetTop, so it's correct regardless of which ancestor is the
    // offsetParent (offsetTop is measured against offsetParent, which may not
    // be this container) and ignores any non-scrolling chrome above it.
    const spotTopWithinContent =
      spotRow.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop;
    // Land the spot row's MIDDLE at the viewport's MIDDLE.
    const target = spotTopWithinContent - container.clientHeight / 2 + spotRow.clientHeight / 2;
    // Clamp to the valid scroll range -- no overscroll past top/bottom; if spot
    // is near an end it lands as close to centre as the range allows.
    const maxScroll = container.scrollHeight - container.clientHeight;
    container.scrollTop = Math.max(0, Math.min(target, maxScroll));
  }, []);

  useEffect(() => {
    centreOnSpot();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- centre on rung-set
    // rebuild only; live `spot` is intentionally excluded so price ticks don't
    // yank the user's scroll position. centreOnSpot is stable (empty deps).
  }, [rungSignature]);

  if (!covered) {
    return (
      <Box sx={{ ...dilettaPanelSx, overflow: 'hidden', borderRadius: '9px' }}>
        <Banner token={token} ccy={ccy} />
        <EmptyState variant="no-data" size="sm" message="No vol surface" />
      </Box>
    );
  }

  if (mtdStatus === 'error') {
    return (
      <Box sx={{ ...dilettaPanelSx, overflow: 'hidden', borderRadius: '9px' }}>
        <Banner token={token} ccy={ccy} />
        <EmptyState variant="no-data" size="sm" message="No MTD data" />
      </Box>
    );
  }

  if (useYearAnchor && ytdStatus === 'error') {
    return (
      <Box sx={{ ...dilettaPanelSx, overflow: 'hidden', borderRadius: '9px' }}>
        <Banner token={token} ccy={ccy} />
        <EmptyState variant="no-data" size="sm" message="No YTD data" />
      </Box>
    );
  }

  const marker = spot ?? somSpot ?? 0;
  const activeRungs = useYearAnchor ? yearRungsProjected : rungs;
  // Show EVERY level in both modes. A triggered (HIT) prediction level is NOT
  // hidden -- the prediction is resolved, so it renders at 100% (via the cells'
  // displayTouchProb) with the "touched" marker, mirroring the pre-rewire board.
  // OTC forward-month rungs never carry a HIT (suppressed in RungRow).
  const upper = activeRungs.filter((r) => r.level >= marker);
  const lower = activeRungs.filter((r) => r.level < marker);

  return (
    <Box sx={{ ...dilettaPanelSx, overflow: 'hidden', borderRadius: '9px' }}>
      {/* Locate arrow shown only when there's a spot to centre on. */}
      <Banner token={token} ccy={ccy} onCentre={spot !== null ? centreOnSpot : undefined} />

      {/* Prediction: Open/High/Low/Latest tiles. OTC: omitted — MTD context is
          irrelevant for forward-month pricing. */}
      {!isOtc && (
        <Stack direction="row" spacing="1px" sx={{ backgroundColor: DILETTA_DIVIDER }}>
          <Tile label="Open" value={anchorSpot} decimals={decimals} />
          <Tile label="High" value={anchorHigh} decimals={decimals} />
          <Tile label="Low" value={anchorLow} decimals={decimals} />
          <Tile label="Latest" value={spot} decimals={decimals} />
        </Stack>
      )}

      <Box sx={{ ...ROW_GRID_SX, alignItems: 'flex-end', pt: 0.75, pb: 0.5 }}>
        <Typography sx={{ minWidth: 0, ...HEAD_SX }}>Level</Typography>
        <Typography sx={{ textAlign: 'right', ...HEAD_SX }}>vs Spot</Typography>
        <Typography sx={{ textAlign: 'right', ...HEAD_SX, color: TEXT_FAINT }}>Imm (BS)</Typography>
        <Typography sx={{ textAlign: 'right', ...HEAD_SX }}>Imm (Local Vol)</Typography>
        <Typography sx={{ textAlign: 'right', ...HEAD_SX }}>Imm (Stoch Vol)</Typography>
      </Box>

      <Box
        ref={scrollRef}
        // height is a React-CONTROLLED inline style (not sx/className) on
        // purpose: a native resize drag mutates element.style.height directly,
        // and only a React-owned inline style can authoritatively re-write it
        // back to the snapped value on the next render. An sx/class height would
        // lose to the drag's inline style and the snap would never show.
        style={{ height: viewportHeight }}
        sx={{
          ...RUNG_SCROLL_SX,
          minHeight: MIN_RUNG_VIEWPORT_HEIGHT,
          // Native vertical resize grip (bottom-right). overflowX:'hidden' +
          // overflowY:'auto' on RUNG_SCROLL_SX give the grip a non-visible
          // overflow on both axes (Chrome only draws it then); the ResizeObserver
          // above snaps + persists whatever the user drags to. Height-only: card
          // width is owned by the 4-col board grid, so horizontal resize isn't
          // clean.
          resize: 'vertical',
        }}
      >
        {ladder === 'unlisted' ? (
          // The token has a feed but does not list the selected expiry (the OTC
          // strip is the union of all tokens' expiries). Show a muted, centred
          // message in the ladder region; the header, tiles and footer stay.
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ color: TEXT_MUTED, fontSize: 11, fontStyle: 'italic' }}>
              No listed expiry
            </Typography>
          </Box>
        ) : (
          <>
            {upper.map((r) => (
              <RungRow
                key={r.level}
                rung={r}
                spot={spot}
                decimals={decimals}
                isOtc={isOtc}
                onPick={handlePick}
              />
            ))}
            <Box ref={spotRowRef}>
              <SpotRow spot={spot} decimals={decimals} />
            </Box>
            {lower.map((r) => (
              <RungRow
                key={r.level}
                rung={r}
                spot={spot}
                decimals={decimals}
                isOtc={isOtc}
                onPick={handlePick}
              />
            ))}
          </>
        )}
      </Box>

      <Stack
        direction="row"
        spacing="1px"
        sx={{ backgroundColor: DILETTA_DIVIDER, borderTop: `1px solid ${BORDER_SUBTLE}` }}
      >
        <Tile label="ATM vol" value={fmtAtmVol(atmVol)} />
        <Tile label="25RR" value={fmtRr25(rr25)} />
        <Tile label="Expiry" value={formatExpiryStamp(expiry)} />
        <Tile label="Time to expiry" value={formatTimeToExpiry(expiry, now)} />
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.75, py: 0.75, borderTop: `1px solid ${BORDER_SUBTLE}` }}
      >
        <Typography sx={{ color: TEXT_DISABLED, fontSize: 8.5 }}>
          {isOtc
            ? 'live one-touch pricing · Black-Scholes + Local Vol + Stoch Vol'
            : useYearAnchor
            ? 'touch prob · year-open anchored · Black-Scholes + Local Vol + Stoch Vol'
            : 'touch prob · month-open anchored · Black-Scholes + Local Vol + Stoch Vol'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <FreshnessReadout lastUpdated={lastUpdated} now={now} />
      </Stack>
    </Box>
  );
};

export default MarketLadderCard;
